import { FulfillmentGroupData } from "../fulfillments/fulfillment-group";
import { FulfillmentGroupStatus } from "../fulfillments/fulfillment-group-status";
import { FulfillmentOrderAddress } from "../fulfillments/fulfillment-order-address";
import { getProviderDisplayName, Provider } from "../order";

export const mergeableStatuses = [
  FulfillmentGroupStatus.Open,
  FulfillmentGroupStatus.OnHold,
  FulfillmentGroupStatus.Scheduled,
  FulfillmentGroupStatus.PaymentPending,
];

export const autoMergeableStatuses = [
  FulfillmentGroupStatus.Open,
  FulfillmentGroupStatus.PaymentPending,
];

// fields that must match for groups to be mergeable
// if you add a field here, must be added as a lowercase token type in atlas search
export const minimumMatchingOriginAddressFields = [
  "address1",
  "address2",
  "city",
  "countryCode",
  "locationId",
  "zip",
  "province",
] as const satisfies (keyof FulfillmentOrderAddress)[];
export type MinimumMatchingOriginAddressField =
  (typeof minimumMatchingOriginAddressFields)[number];

const originAddressesEqual = (
  a: FulfillmentOrderAddress,
  b: FulfillmentOrderAddress,
) =>
  minimumMatchingOriginAddressFields.every((k) => {
    const valA = a[k] ?? null;
    const valB = b[k] ?? null;

    return (
      (typeof valA === "string" ? valA.toLowerCase() : valA) ===
      (typeof valB === "string" ? valB.toLowerCase() : valB)
    );
  });

export interface Mergeability {
  canMerge: boolean;
  reason: string;
}

/**
 * 🚨 IMPORTANT:
 * If you change the logic in this function, you MUST update
 * `getMinimumMergeableSearchCompound` below to ensure consistency
 * in merging rules.
 */
export function canMergeFulfillmentGroupData(
  groupData: readonly FulfillmentGroupData[],
  ignoreRatePreparation?: boolean,
): Mergeability {
  const reasons: string[] = [];

  const anyNeedsRatePreparation = groupData.some(
    (group) => group.needsRatePreparation,
  );
  if (anyNeedsRatePreparation && !ignoreRatePreparation) {
    reasons.push("Some shipments have not yet finished running automations");
  }

  const someAssignedToPickSession = groupData.some(
    (group) => group.pickSessionId,
  );

  if (someAssignedToPickSession) {
    reasons.push(
      "One or more shipments are assigned to a pick session and cannot be merged",
    );
  }

  const allSameFulfillmentService = groupData.every(
    (group) =>
      group.assignedFulfillmentService?._id ===
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      groupData[0].assignedFulfillmentService?._id,
  );
  if (!allSameFulfillmentService) {
    reasons.push(
      "All shipments must be assigned to the same fulfillment service",
    );
  }

  const allSameTeam = groupData.every(
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    (group) => group.teamInfo?._id === groupData[0].teamInfo?._id,
  );
  if (!allSameTeam) {
    reasons.push("All shipments must be from the same store");
  }

  const statusesCanMerge = groupData.every(
    (group) => group?.status && mergeableStatuses.includes(group?.status),
  );
  if (!statusesCanMerge) {
    reasons.push("All shipments must be in a mergeable status");
  }

  const sameOriginAddress = groupData.every((group) =>
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    originAddressesEqual(group.originAddress, groupData[0].originAddress),
  );
  if (!sameOriginAddress) {
    reasons.push("All shipments must have the same origin address");
  }

  const allFulfillmentOrderIds = groupData.flatMap(
    (group) => group.fulfillmentOrderIds,
  );
  const uniqueFulfillmentOrderIds = new Set(allFulfillmentOrderIds);

  if (allFulfillmentOrderIds.length !== uniqueFulfillmentOrderIds.size) {
    // this should never happen
    reasons.push("Fulfillment order IDs must be unique");
  }

  if (
    groupData.every((group) =>
      Object.values(group.fulfillmentOrderToDeliveryMethod).every(
        (method) => method.kind === "NONE",
      ),
    )
  ) {
    reasons.push("Some shipment missing a delivery method");
  }
  const allSameLandedCost = groupData.every(
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    (group) => !!group.hasLandedCost === !!groupData[0].hasLandedCost,
  );
  if (!allSameLandedCost) {
    reasons.push(
      "All shipments must have the same landed cost (DDP) status at checkout",
    );
  }

  const providers = groupData.flatMap((group) => group.providers);

  const uniqueProviders = new Set(providers);
  if (uniqueProviders.size > 1) {
    reasons.push("All shipments must have the same source");
  }

  // Ebay and TikTok do not allow uploading the same tracking info to multiple orders unless they are for the same customer
  const customerIdRequiredProvider = [Provider.EBAY, Provider.TIKTOK].find(
    (p) => providers.includes(p),
  );
  if (customerIdRequiredProvider) {
    const customerIds = [
      ...new Set(groupData.map((group) => group.customer?.id)),
    ];

    if (customerIds.length !== 1 || customerIds.some((id) => !id)) {
      reasons.push(
        `All ${getProviderDisplayName(customerIdRequiredProvider)} shipments must have the same customer ID`,
      );
    }
  }

  return { canMerge: reasons.length === 0, reason: reasons.join("; ") };
}
